import { ProjectTypes } from 'constants/enums'
import type { NavValue } from 'models/priceChart'
import type { Project } from 'models/project'

/**
 * Digits, no thousands separators, with the fraction capped to `maxDecimals` (the
 * currency's precision — 2 for fiat, 8 for BTC, etc). `maxDecimals === 0` drops the
 * decimal point entirely. Omitting it leaves the fraction uncapped.
 */
export const sanitizeAmount = (value: string, maxDecimals?: number): string => {
  const cleaned = value.replace(/[^\d.]/g, '')
  const [whole, ...rest] = cleaned.split('.')
  if (!rest.length) return whole
  if (maxDecimals === 0) return whole
  const fraction = rest.join('')
  return `${whole}.${maxDecimals != null ? fraction.slice(0, maxDecimals) : fraction}`
}

/** The invest-flow entry, with the prefill carried as query params: invest-in-sto preselects the
 *  currency (a back-office ref) and starts its calculator with the amount. */
export const buildInvestUrl = (base: string, slug: string, amount: string, currency: string): string => {
  const params = new URLSearchParams()
  if (amount) params.set('amount', amount)
  if (currency) params.set('currency', currency)
  const query = params.toString()
  return query ? `${base}/${slug}?${query}` : `${base}/${slug}`
}

export interface SecuritiesPrice {
  /** Price of one security in the project currency. */
  tokenPrice: number
  /** The token's precision (`tokenRoundingDecimals`). */
  decimals: number
}

/** The newest NAV with a price. */
export const latestNavPrice = (values: NavValue[] | undefined): number | undefined => {
  let latest: { time: number; price: number } | undefined
  for (const value of values ?? []) {
    const time = new Date(value.time).getTime()
    const price = Number(value.price)
    if (!Number.isFinite(time) || !Number.isFinite(price) || price <= 0) continue
    if (!latest || time > latest.time) latest = { time, price }
  }
  return latest?.price
}

/**
 * The price invest-in-sto's calculator converts with: the public sale's token price, or for
 * a fund the latest NAV (its sale price is unreliable). Undefined until it is known.
 */
export const getSecuritiesPrice = (
  project: Pick<Project, 'type' | 'sales' | 'tokenRoundingDecimals'> | undefined,
  navPrice: number | undefined,
): SecuritiesPrice | undefined => {
  if (!project?.type) return undefined
  const tokenPrice =
    project.type === ProjectTypes.FUND ? navPrice : Number(project.sales?.find((sale) => sale.isVariable)?.tokenPrice)
  if (tokenPrice === undefined || !Number.isFinite(tokenPrice) || tokenPrice <= 0) return undefined
  return { tokenPrice, decimals: Number(project.tokenRoundingDecimals ?? 0) || 0 }
}

/**
 * Amount ÷ price, floored to the token's precision as invest-in-sto does; empty without a positive
 * amount. `rate`: one unit of the amount's currency in the project's (1 when it is the project's).
 */
export const securitiesForAmount = (amount: string, price: SecuritiesPrice, rate = 1): string => {
  const value = Number(amount)
  if (!amount.trim() || !Number.isFinite(value) || value <= 0) return ''
  const factor = 10 ** price.decimals
  // toPrecision first so float noise (0.3 / 0.1 = 2.999…) doesn't floor a whole security away.
  const scaled = Number((((value * rate) / price.tokenPrice) * factor).toPrecision(12))
  return (Math.floor(scaled) / factor).toFixed(price.decimals)
}

/** A minimum in the project's currency, in another one: rounded up, so paying it meets the minimum. */
export const minimumInCurrency = (minimum: number, rate: number, decimals: number): number => {
  const factor = 10 ** decimals
  return Math.ceil(Number(((minimum / rate) * factor).toPrecision(12))) / factor
}
